'use strict'

// Create / invite a new human team member — opened from the Team & agents
// canvas "+" menu (root._createMemberOpen). Mirrors CreateAgentModal: the
// shared Modal chrome (backdrop, Escape-to-close, stateKey contract, URL
// restore) with a ModalWindow body.
//
// Fields:
//   Email — required; the invitee's address. They receive an org invitation
//           and, once accepted, appear on the Team & agents canvas.
//   Role  — org role preset (member | admin), passed straight to the
//           invitation.
//
// Submit invites via el.sdk.createOrgInvitation(orgId, { email, role }) —
// the org-level invitation API (OrganizationService). orgId is the active
// org from the JWT claims, the same source the canvas + assignments use.
const _resetMember = {
  _createMemberOpen: false,
  _createMemberEmail: '',
  _createMemberRole: 'member',
  _createMemberPending: false,
  _createMemberError: null
}

export const CreateMemberModal = {
  extends: 'Modal',
  stateKey: '_createMemberOpen',
  // Escape/backdrop close also clears the draft (shared Modal merges
  // extraClose into its close update).
  extraClose: {
    _createMemberEmail: '',
    _createMemberRole: 'member',
    _createMemberPending: false,
    _createMemberError: null
  },

  Window: {
    // The centre sheet (DESIGN_SPEC §6.1): the `overlayMd` width token, capped
    // by the veil's inset box — the shared Modal owns the veil, the sheet
    // motion, the phone sheet and the focus/Escape contract.
    width: 'overlayMd',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    flow: 'y',
    gap: 'A',

    ModalHeader: {
      Hgroup: {
        H: { text: '{{ workspace.createMember.title | polyglot }}' },
        P: {
          text: '{{ workspace.createMember.subtitle | polyglot }}'
        }
      }
    },

    Form: {
      tag: 'form',
      novalidate: true,
      flow: 'y',
      gap: 'A',
      onSubmit: async (e, el, s) => {
        e.preventDefault()
        const root = s.root
        const email = String(root?._createMemberEmail || '').trim()
        const role = root?._createMemberRole || 'member'
        if (!email) return
        const orgId =
          root?.jwtClaims?.workspace_org_id ||
          (root?.jwtClaims?.orgs && root.jwtClaims.orgs[0]?.id) ||
          null
        if (!orgId) {
          root.update({
            _createMemberError: el.call('polyglot', 'workspace.createMember.noOrg')
          })
          return
        }
        root.update({ _createMemberPending: true, _createMemberError: null })
        try {
          await el.sdk.createOrgInvitation(orgId, { email, role })
          el.call('openNotification', {
            title: el.call('polyglot', 'workspace.createMember.sent'),
            message: el
              .call('polyglot', 'workspace.createMember.sentBody')
              .replace('{email}', email),
            type: 'success'
          })
          root.update({ ..._resetMember })
          // the invite form's fields are saved (kit dirty clears)
          el.call('markPageSaved', el)
        } catch (err) {
          console.warn('[CreateMemberModal] invite failed', err)
          root.update({
            _createMemberPending: false,
            _createMemberError:
              (err && err.message) || el.call('polyglot', 'workspace.createMember.inviteFailed')
          })
        }
      },

      // Both fields are the shared field kit (shared/components/AppField.js)
      // — the kit owns the label and the control surface; this form keeps
      // the behaviour only. `label` is a FUNCTION prop: the kit also names
      // the control with it, and an attribute never resolves a template.
      EmailField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createMember.email'),
        AppInput: {
          type: 'email',
          placeholder: (el) => el.call('polyglot', 'workspace.createMember.emailPlaceholder'),
          value: (el, s) => s.root?._createMemberEmail || '',
          onInput: (e, el, s) => s.root.update({ _createMemberEmail: e.target.value })
        }
      },

      RoleField: {
        extends: 'AppField',
        label: (el) => el.call('polyglot', 'workspace.createMember.role'),
        // Native <select> (the kit's AppSelect), CONTROLLED: `value` comes
        // from state, and the framework re-applies a declared select value
        // after the options mount and after every option reconcile (smbls
        // element create.js, syncSelectValue). The node pins this replaced
        // ran only on render/update, so a reconcile left the browser showing
        // ADMIN while the invite carried MEMBER (live on dev, 2026-09-24).
        AppSelect: {
          name: 'member-role',
          value: (el, s) => s.root?._createMemberRole || 'member',
          onChange: (e, el, s) => s.root.update({ _createMemberRole: e.target.value }),
          // A STATIC list: each option's words are a text template, which
          // switches language on its own text effect. A list built with
          // polyglot inside `children` re-ran on every workspace signal
          // (translate() walks `state.workspace` for a `workspace.*` key).
          childrenAs: 'props',
          children: () => [
            { value: 'member', text: '{{ workspace.createMember.roleMember | polyglot }}' },
            { value: 'admin', text: '{{ workspace.createMember.roleAdmin | polyglot }}' }
          ],
          childProps: { tag: 'option' }
        }
      },

      ErrorNote: {
        tag: 'p',
        role: 'alert',
        hide: (el, s) => !s.root?._createMemberError,
        margin: '0',
        fontSize: 'Y',
        lineHeight: '1.4',
        // The AA-safe danger pair (the raw brand `red` read under 4.5:1 as
        // text in light).
        color: 'dangerInk',
        text: (el, s) => s.root?._createMemberError || ''
      },

      ModalFooter: {
        flow: 'x',
        gap: 'Z',
        align: 'center',
        justifyContent: 'flex-end',

        // The kit footer (DESIGN_SPEC §6.9, L68): Cancel ghost · primary ink.
        // The kit owns the scale, the washes and the disabled step — never
        // an opacity (Rule 65).
        AppCapsuleBtnGhost: {
          text: '{{ workspace.createMember.cancel | polyglot }}',
          onClick: (e, el, s) => {
            e.preventDefault()
            s.root.update({ ..._resetMember })
          }
        },

        AppCapsuleBtnPrimary: {
          type: 'submit',
          // The house floor for a primary whose label changes while it works
          // ("Send invite" → "Inviting…"), W1's CRM footers alike.
          minWidth: 'E',
          disabled: (el, s) =>
            !(s.root?._createMemberEmail || '').trim() || !!s.root?._createMemberPending,
          text: (el, s) =>
            el.call(
              'polyglot',
              s.root?._createMemberPending
                ? 'workspace.createMember.submitting'
                : 'workspace.createMember.submit'
            )
        }
      }
    }
  }
}
